import { useEffect, useState } from 'react';
import { RoomCard } from './RoomCard.js';
import type { RoomData } from './RoomCard.js';
import { colyseusClient } from '../../api/colyseus.js';

interface PublicRoomsPanelProps {
  onJoinRoom: (roomId: string) => void;
}

export function PublicRoomsPanel({ onJoinRoom }: PublicRoomsPanelProps) {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'beginner' | 'fast' | 'private'>('all');
  const [rooms, setRooms] = useState<RoomData[]>([]);

  // Fetch active rooms from Colyseus matchmaking service
  useEffect(() => {
    let isMounted = true;

    async function fetchRooms() {
      try {
        const availableRooms = await colyseusClient.getAvailableRooms('game');
        if (!isMounted) return;

        const parsedRooms: RoomData[] = availableRooms.map((room) => {
          const metadata = room.metadata || {};
          return {
            id: room.roomId,
            name: metadata.name || `Room ${room.roomId}`,
            playersCount: room.clients,
            maxPlayers: room.maxClients,
            speed: metadata.speed || 'normal',
            status: 'Waiting',
            type: room.clients >= room.maxClients ? 'private' : 'public',
          };
        });

        setRooms(parsedRooms);
      } catch (err) {
        console.error('Failed to query available Colyseus rooms:', err);
      }
    }

    fetchRooms();
    const interval = setInterval(fetchRooms, 3500);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Filter logic
  const filteredRooms = rooms.filter((room) => {
    const matchesSearch = room.name.toLowerCase().includes(search.toLowerCase());
    const matchesTab = activeTab === 'all' || room.type === activeTab;
    return matchesSearch && matchesTab;
  });

  return (
    <div className="glass rounded-3xl p-5 border border-white/10 shadow-glow-lg flex flex-col h-full text-left overflow-hidden">
      {/* Panel Header & Search */}
      <div className="space-y-3 mb-3">
        <h3 className="font-display text-base font-black tracking-wider text-amber-300 uppercase">
          Public Rooms
        </h3>

        {/* Search Bar Input */}
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search room name..."
            className="w-full pl-9 pr-3 py-2 text-xs rounded-xl bg-slate-950/40 border border-white/5 text-white placeholder-slate-500 font-medium focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/30 transition-all duration-300 shadow-inner"
          />
          <span className="absolute left-3 top-2.5 text-xs opacity-40">🔍</span>
        </div>

        {/* Filter Tabs list */}
        <div className="flex border-b border-white/5 pb-1 gap-1">
          {(['all', 'beginner', 'fast', 'private'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                activeTab === tab
                  ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30 shadow-inner font-black'
                  : 'text-surface-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable list items */}
      <div className="flex-1 overflow-y-auto pr-1 space-y-2 max-h-[260px]">
        {filteredRooms.length > 0 ? (
          filteredRooms.map((room) => (
            <RoomCard key={room.id} room={room} onJoin={onJoinRoom} />
          ))
        ) : (
          /* Custom CSS-based Empty state illustration */
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            {/* CSS Shapes Illustration representing a ticket and magnifying glass */}
            <div className="relative w-16 h-16 mb-4 flex items-center justify-center bg-purple-950/30 border border-purple-500/10 rounded-2xl shadow-inner">
              <div className="w-10 h-6 border border-purple-500/20 rounded bg-purple-500/5 flex items-center justify-center">
                <span className="text-[7px] text-purple-400/40 font-mono">🎫</span>
              </div>
              <div className="absolute bottom-2 right-2 w-6 h-6 rounded-full border-2 border-amber-500/50 bg-slate-900 flex items-center justify-center shadow-md">
                <span className="text-[9px]">🔍</span>
              </div>
            </div>
            <p className="text-[11px] font-bold text-white/80 tracking-wide mb-1 uppercase">
              No Rooms Found
            </p>
            <p className="text-[10px] text-surface-400 leading-relaxed font-medium">
              No public lobbies match your filters. Create one to invite your friends!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
